import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase, DB_SCHEMA } from '../supabase/config';

interface AcademicYearContextType {
  // Current working year
  workingYear: number;
  setWorkingYear: (year: number) => void;

  // Available years (years that have grids)
  availableYears: number[];

  // Loading state
  isLoading: boolean;

  // Helper to get display format
  getYearDisplay: (year: number) => string;
}

const STORAGE_KEY = 'pit_working_academic_year';

// Get current calendar year - default to next year for planning
const getDefaultYear = (): number => {
  const now = new Date();
  const currentMonth = now.getMonth(); // 0-11
  // If we're in the second half of the year (July onwards), default to next year
  // since schools typically plan ahead
  if (currentMonth >= 6) {
    return now.getFullYear() + 1;
  }
  return now.getFullYear();
};

export const AcademicYearContext = createContext<AcademicYearContextType | undefined>(undefined);

interface AcademicYearProviderProps {
  children: ReactNode;
}

export function AcademicYearProvider({ children }: AcademicYearProviderProps) {
  // Initialize from localStorage or default to current/next year
  const [workingYear, setWorkingYearState] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed) && parsed >= 2020 && parsed <= 2100) {
          return parsed;
        }
      }
    } catch (e) {
      // localStorage might not be available
    }
    return getDefaultYear();
  });

  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Persist working year to localStorage
  const setWorkingYear = useCallback((year: number) => {
    setWorkingYearState(year);
    try {
      localStorage.setItem(STORAGE_KEY, year.toString());
    } catch (e) {
      // localStorage might not be available
    }
  }, []);

  // Load available years from database
  useEffect(() => {
    const loadAvailableYears = async () => {
      setIsLoading(true);
      try {
        // Get distinct academic_year values from grids table
        const { data, error } = await supabase
          .from('grids')
          .select('academic_year')
          .order('academic_year', { ascending: false });

        if (error) {
          console.error('Error loading available years:', error);
          // Fall back to current year if there's an error
          setAvailableYears([getDefaultYear()]);
        } else {
          // Extract unique years
          const years = [...new Set(data?.map(g => g.academic_year).filter(Boolean))] as number[];

          // Always include the working year in the list
          if (!years.includes(workingYear)) {
            years.push(workingYear);
          }

          // Sort descending (most recent first)
          years.sort((a, b) => b - a);

          setAvailableYears(years);

          // If working year is not in available years, switch to the most recent
          if (years.length > 0 && !years.includes(workingYear)) {
            setWorkingYear(years[0]);
          }
        }
      } catch (error) {
        console.error('Error loading available years:', error);
        setAvailableYears([getDefaultYear()]);
      } finally {
        setIsLoading(false);
      }
    };

    loadAvailableYears();

    // Subscribe to grid changes to update available years
    const subscription = supabase
      .channel('grids-year-changes')
      .on('postgres_changes',
        { event: '*', schema: DB_SCHEMA, table: 'grids' },
        () => {
          loadAvailableYears();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [workingYear, setWorkingYear]);

  // Helper to get display format
  const getYearDisplay = useCallback((year: number): string => {
    return `${year}`;
  }, []);

  const value: AcademicYearContextType = {
    workingYear,
    setWorkingYear,
    availableYears,
    isLoading,
    getYearDisplay,
  };

  return (
    <AcademicYearContext.Provider value={value}>
      {children}
    </AcademicYearContext.Provider>
  );
}

// Hook to use the AcademicYear context
export const useAcademicYear = () => {
  const context = useContext(AcademicYearContext);
  if (context === undefined) {
    throw new Error('useAcademicYear must be used within an AcademicYearProvider');
  }
  return context;
};
